import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import { DN_STATUSES, DISPATCH_STATUSES } from "../lib/deliveryNotes";
import { todayISO } from "../lib/dates";

const num = (n) => Number(n) || 0;
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const outstandingOf = (l) => Math.max(0, round3(num(l.ordered_quantity) - num(l.previously_dispatched) - num(l.dispatched_quantity)));
const capOf = (l) => {
  const byOrder = Math.max(0, round3(num(l.ordered_quantity) - num(l.previously_dispatched)));
  return num(l.reserved_quantity) > 0 ? Math.min(num(l.reserved_quantity), byOrder) : byOrder;
};

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder }) {
  const id = useMemo(() => `dn-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

// Build deliverable lines from a sales order's lines (reserved = the dispatch cap;
// allocated_quantity = already dispatched on earlier notes).
const linesFromSO = (so) =>
  (so?.sales_order_lines || [])
    .map((l) => {
      const reserved = num(l.reserved_quantity), prev = num(l.allocated_quantity);
      return {
        sales_order_line_id: l.id, product_id: l.product_id || null, sku: l.sku || "", description: l.description || "",
        unit_price: l.unit_price ?? 0, ordered_quantity: num(l.quantity), reserved_quantity: reserved,
        previously_dispatched: prev, dispatched_quantity: reserved, delivered_quantity: 0, returned_quantity: 0,
      };
    })
    .filter((l) => l.reserved_quantity > 0 || l.previously_dispatched > 0);

/**
 * Create/Edit a Delivery Note. Receives dispatchable sales orders as a prop (loaded by
 * the manager via the data layer) — never touches Supabase directly. Dispatch is capped
 * at the reserved quantity, so you can never dispatch above what's reserved.
 */
export default function DeliveryNoteForm({ initial, dispatchableSOs = [], onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const editSO = seed.sales_orders;
  const [f, setF] = useState({
    customer_id: seed.customer_id || "",
    customer_name: seed.customer_name || "",
    sales_order_id: seed.sales_order_id || "",
    sales_order_number: seed.sales_order_number || "",
    delivery_date: seed.delivery_date || todayISO(),
    driver: seed.driver || "",
    vehicle: seed.vehicle || "",
    reference: seed.reference || "",
    status: seed.status || "dispatched",
    notes: seed.notes || "",
  });
  const [lines, setLines] = useState((seed.delivery_note_lines || []).map((l) => ({ ...l })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const moves = DISPATCH_STATUSES.includes(f.status);

  const onSO = (id) => {
    const so = dispatchableSOs.find((s) => s.id === id);
    const newLines = linesFromSO(so);
    setF((p) => ({ ...p, sales_order_id: id || "", sales_order_number: so?.so_number || "", customer_id: so?.customer_id || null, customer_name: so?.customer_name || "" }));
    setLines(newLines);
  };
  const updLine = (i, patch) => setLines((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const sum = lines.reduce((a, l) => ({
    dispatched: a.dispatched + num(l.dispatched_quantity), returned: a.returned + num(l.returned_quantity), outstanding: a.outstanding + outstandingOf(l),
  }), { dispatched: 0, returned: 0, outstanding: 0 });

  const submit = async () => {
    setError("");
    if (!f.sales_order_id) { setError("Please select a sales order."); return; }
    if (!lines.length) { setError("This sales order has nothing reserved to dispatch."); return; }
    setBusy(true);
    try {
      await onSave({ ...f, customer_id: f.customer_id || null, sales_order_id: f.sales_order_id || null, lines: lines.map(({ id, outstanding_quantity, position, ...rest }) => rest) });
    } catch (e) { setError(e.message || "Could not save delivery note"); setBusy(false); }
  };

  const cell = { ...S.input, padding: "6px 8px", fontSize: 12 };
  const ro = { ...cell, background: C.surface, color: C.textMuted };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 940 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Delivery Note" : "New Delivery Note"}
            {seed.dn_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.dn_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={S.grid2}>
          {isEdit ? (
            <div><label style={S.label}>Sales Order</label><input style={{ ...S.input, background: C.surface, color: C.textMuted }} value={editSO?.so_number || seed.sales_order_number || "—"} readOnly /></div>
          ) : (
            <Field label="Sales Order *" value={f.sales_order_id} onChange={onSO}
              options={[{ value: "", label: dispatchableSOs.length ? "Select a sales order…" : "No orders with reserved stock" }, ...dispatchableSOs.map((s) => ({ value: s.id, label: `${s.so_number} — ${s.customer_name || ""}` }))]} />
          )}
          <Field label="Delivery Status" value={f.status} onChange={set("status")} options={DN_STATUSES} />
          <Field label="Delivery Date" type="date" value={f.delivery_date} onChange={set("delivery_date")} />
          <Field label="Reference" value={f.reference} onChange={set("reference")} placeholder="Customer ref / order no." />
          <Field label="Driver" value={f.driver} onChange={set("driver")} />
          <Field label="Vehicle" value={f.vehicle} onChange={set("vehicle")} placeholder="Reg / van" />
        </div>

        {(f.customer_name || f.sales_order_number) && (
          <div style={{ marginTop: 12, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.textMuted, display: "flex", gap: 18, flexWrap: "wrap" }}>
            {f.customer_name && <span><strong style={{ color: C.text }}>{f.customer_name}</strong></span>}
            {f.sales_order_number && <span>📄 {f.sales_order_number}</span>}
            <span style={{ color: moves ? C.warning : C.textDim }}>{moves ? "This status dispatches stock" : "This status does not move stock yet"}</span>
          </div>
        )}

        <div style={S.sectionTitle}>Items to dispatch</div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 760 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.8fr 90px 70px 70px 80px 70px 80px", gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: C.textDim }}>
              {["Product", "SKU", "Ordered", "Reserved", "Dispatch", "Return", "Outstanding"].map((h) => <div key={h}>{h}</div>)}
            </div>
            {lines.length === 0 ? (
              <div style={{ ...S.empty, padding: "20px 0" }}>{isEdit ? "No lines." : "Select a sales order to load reserved items."}</div>
            ) : lines.map((l, i) => {
              const cap = capOf(l);
              const over = num(l.dispatched_quantity) > cap;
              return (
                <div key={i}>
                  <div style={{ display: "grid", gridTemplateColumns: "1.8fr 90px 70px 70px 80px 70px 80px", gap: 6, marginBottom: 4, alignItems: "center" }}>
                    <input style={cell} value={l.description || ""} placeholder="Description" onChange={(e) => updLine(i, { description: e.target.value })} />
                    <input style={ro} value={l.sku || ""} readOnly />
                    <input style={ro} value={l.ordered_quantity} readOnly />
                    <input style={ro} value={l.reserved_quantity} readOnly />
                    <input style={{ ...cell, ...(over ? { borderColor: C.danger } : {}) }} type="number" value={l.dispatched_quantity} onChange={(e) => updLine(i, { dispatched_quantity: +e.target.value })} />
                    <input style={cell} type="number" value={l.returned_quantity} onChange={(e) => updLine(i, { returned_quantity: +e.target.value })} />
                    <input style={ro} value={outstandingOf(l)} readOnly />
                  </div>
                  {over && <div style={{ fontSize: 11, color: C.danger, marginBottom: 6 }}>⚠ Cannot dispatch above reserved ({cap}) — it will be capped on save.</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Live dispatch summary */}
        {lines.length > 0 && (
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", background: C.surface2, borderRadius: 8, padding: "10px 16px", marginTop: 12, fontSize: 13 }}>
            <span style={{ color: C.textMuted }}>Dispatching <strong style={{ color: C.success }}>{round3(sum.dispatched)}</strong></span>
            <span style={{ color: C.textMuted }}>Returning <strong style={{ color: sum.returned > 0 ? C.warning : C.text }}>{round3(sum.returned)}</strong></span>
            <span style={{ color: C.textMuted }}>Outstanding after <strong style={{ color: sum.outstanding > 0 ? C.warning : C.success }}>{round3(sum.outstanding)}</strong></span>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Notes</label>
          <textarea style={{ ...S.input, height: 48 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Delivery Note"}
          </button>
        </div>
      </div>
    </div>
  );
}
